const imageCache = new Map<string, string | null>();

export interface ImageCropInsets {
  /** 上から切り落とす割合（縦を100%とした場合） */
  top: number;
  /** 下から切り落とす割合（縦を100%とした場合） */
  bottom: number;
}

/** PDF用デフォルト: 上40% / 下10% を切り、縦50%帯から正方形を切り出す */
export const DEFAULT_IMAGE_CROP_INSETS: ImageCropInsets = {
  top: 0.40,
  bottom: 0.10,
};

export type WineImageLoadOptions = {
  centerCrop?: boolean;
  cropInsets?: Partial<ImageCropInsets>;
};

const clampInset = (value: number) => Math.max(0, Math.min(0.49, value));

const resolveCropInsets = (partial?: Partial<ImageCropInsets>): ImageCropInsets => ({
  top: clampInset(partial?.top ?? DEFAULT_IMAGE_CROP_INSETS.top),
  bottom: clampInset(partial?.bottom ?? DEFAULT_IMAGE_CROP_INSETS.bottom),
});

const cropCacheKey = (url: string, insets: ImageCropInsets) =>
  `${url}::crop:T${insets.top}-B${insets.bottom}:square`;

/**
 * 縦を基準に帯を決め、その中から正方形を切り出す（アスペクト比を崩さない）。
 * 例: top=40% / bottom=10% → 高さのやや下寄り50%帯。横は中央揃え。
 */
export const cropImageCenter = (
  source: string | HTMLImageElement,
  cropInsets: Partial<ImageCropInsets> = DEFAULT_IMAGE_CROP_INSETS
): Promise<string | null> =>
  new Promise((resolve) => {
    const insets = resolveCropInsets(cropInsets);

    const draw = (img: HTMLImageElement) => {
      const width = img.naturalWidth || img.width;
      const height = img.naturalHeight || img.height;
      if (!width || !height) {
        resolve(null);
        return;
      }

      const bandTop = height * insets.top;
      const bandHeight = height * (1 - insets.top - insets.bottom);
      if (bandHeight <= 0) {
        resolve(null);
        return;
      }

      // 正方形ソース領域（縦長でも横長でも歪まない）
      const side = Math.min(width, bandHeight);
      const sx = (width - side) / 2;
      const sy = bandTop + (bandHeight - side) / 2;

      const canvas = document.createElement('canvas');
      const outSize = Math.max(64, Math.min(256, Math.round(side)));
      canvas.width = outSize;
      canvas.height = outSize;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }
      ctx.drawImage(img, sx, sy, side, side, 0, 0, outSize, outSize);
      try {
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      } catch {
        resolve(null);
      }
    };

    if (typeof source !== 'string') {
      draw(source);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => draw(img);
    img.onerror = () => resolve(null);
    img.src = source;
  });

const loadViaFetch = async (url: string): Promise<string | null> => {
  const response = await fetch(url);
  if (!response.ok) {
    return null;
  }
  const blob = await response.blob();
  if (!blob.type.startsWith('image/')) {
    return null;
  }
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
};

const loadViaCanvas = (url: string): Promise<string | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }
      ctx.drawImage(img, 0, 0);
      try {
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

export const loadImageDataUrl = async (
  url: string,
  options: WineImageLoadOptions = {}
): Promise<string | null> => {
  if (!url) {
    return null;
  }
  const insets = resolveCropInsets(options.cropInsets);
  const cacheKey = options.centerCrop ? cropCacheKey(url, insets) : url;
  if (imageCache.has(cacheKey)) {
    return imageCache.get(cacheKey) ?? null;
  }
  let dataUrl: string | null = null;
  try {
    dataUrl = await loadViaFetch(url);
  } catch {
    dataUrl = null;
  }
  if (!dataUrl) {
    try {
      dataUrl = await loadViaCanvas(url);
    } catch {
      dataUrl = null;
    }
  }
  if (dataUrl && options.centerCrop) {
    dataUrl = (await cropImageCenter(dataUrl, insets)) ?? dataUrl;
  }
  imageCache.set(cacheKey, dataUrl);
  return dataUrl;
};

export const preloadWineImages = async (
  wines: { image?: string }[],
  options: WineImageLoadOptions = {}
): Promise<void> => {
  const urls = [...new Set(wines.map((wine) => wine.image).filter((url): url is string => Boolean(url)))];
  await Promise.all(urls.map((url) => loadImageDataUrl(url, options)));
};

export const getCachedImageDataUrl = (
  url: string,
  options: WineImageLoadOptions = {}
): string | null => {
  if (!url) {
    return null;
  }
  const insets = resolveCropInsets(options.cropInsets);
  const cacheKey = options.centerCrop ? cropCacheKey(url, insets) : url;
  if (!imageCache.has(cacheKey)) {
    return null;
  }
  return imageCache.get(cacheKey) ?? null;
};

export const getImageFormat = (dataUrl: string): 'JPEG' | 'PNG' => {
  if (dataUrl.startsWith('data:image/png')) {
    return 'PNG';
  }
  return 'JPEG';
};
